/* The /cli-login route — where a CLI sends a user to sign in and approve a terminal.
 * Shared by the AI21 Gateway and hopt CLIs, which name themselves with `?client=`.
 * A public sibling of /login (see Router.tsx): it manages its own auth-status redirect,
 * and it must not sit inside the gate, whose MobileGuard and provider stack it has no
 * use for.
 *
 * Wiring only. The validation lives in cliLoginRequest.ts and the screen is presentational,
 * so the rules that matter here are unit-testable without rendering anything.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuthStatus, useAuthUser } from "./session";
import { AuthSplash } from "./AuthSplash";
import { CliLoginScreen, CliLoginRefused } from "./CliLoginScreen";
import { parseCliLoginRequest } from "./cliLoginRequest";
import { CLI_LOGIN_PATH, LOGIN_PATH } from "./paths";
import { AUTH_ENABLED } from "./config";

/* Whether this document is the top-level one. The consent button is the entire security
 * boundary of the CLI login, and the repo ships no CSP at all, so a framed copy of this
 * page could be positioned under someone's cursor. The Caddyfile sets
 * `frame-ancestors 'none'` for this route, which is the real defence; this check also
 * holds in local development, where Caddy is not in front of the app. */
function isFramed(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    // Cross-origin access to window.top throws — which itself means we are framed.
    return true;
  }
}

export function CliLoginRoute() {
  // Auth-off deployments have no Firebase and therefore no refresh token to hand over;
  // there is nothing this page could do.
  if (!AUTH_ENABLED) return <Navigate to="/" replace />;

  return <CliLoginGate />;
}

function CliLoginGate() {
  const { status } = useAuthStatus();
  const user = useAuthUser();
  const location = useLocation();

  if (isFramed()) return <CliLoginRefused reason="framed" />;

  // Parsed before the auth check, so a malformed request is refused outright rather than
  // sending the user through a sign-in that was never going to be honoured.
  const parsed = parseCliLoginRequest(location.search);
  if (!parsed.ok) return <CliLoginRefused reason={parsed.refusal} />;

  if (status === "loading") return <AuthSplash />;

  // Not signed in: sign in first, then come back here with the request intact.
  // `nextAfterAuth`'s `redirect` only honours same-app relative paths, and this is one.
  if (status === "anonymous" || status === "unverified") {
    const back = encodeURIComponent(`${CLI_LOGIN_PATH}${location.search}`);
    return <Navigate to={`${LOGIN_PATH}?redirect=${back}`} replace />;
  }

  if (!user) return <AuthSplash />;

  return <CliLoginScreen request={parsed.request} user={user} />;
}
